import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { useSettings } from "@/lib/SettingsContext";
import "./home.css";

const U = (id: string, w = 1600, q = 85) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&q=${q}&auto=format&fit=crop`;

const HERO_FALLBACK = [
  U("1429962714451-bb934ecdc4ec", 1920, 88),
  U("1470229722913-7c0e2dbbafd3", 1920, 88),
  U("1516450360452-9312f5e86fc7", 1920, 88),
  U("1514525253161-7a46d19cd819", 1920, 88),
  U("1505373877841-8d25f7d46678", 1920, 88),
];

const ABOUT_IMG = U("1492684223066-81342ee5ff30", 1200);
const PHILOSOPHY_IMG = U("1429962714451-bb934ecdc4ec", 1400);

const EVENTS = [
  { label: "Corporate Events",   cat: "corporate",   img: U("1511578314322-379afb476865"), settingKey: "event_img_corporate" },
  { label: "Live Concerts",      cat: "concerts",    img: U("1470229722913-7c0e2dbbafd3"), settingKey: "event_img_concerts" },
  { label: "Government & Gala",  cat: "gala",        img: U("1519167758481-83f550bb49b3"), settingKey: "event_img_gala" },
  { label: "Exhibitions",        cat: "exhibitions", img: U("1505373877841-8d25f7d46678"), settingKey: "event_img_exhibitions" },
  { label: "Product Launches",         cat: "concerts",    img: U("1516450360452-9312f5e86fc7"), settingKey: "event_img_launches" },
  { label: "Social Events",      cat: "social",      img: U("1464366400600-7168b8af9bc3"), settingKey: "event_img_social" },
];

const ServiceIcons: Record<string, React.ReactNode> = {
  "audio": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
  "lighting": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/></svg>,
  "led-video": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  "projection-mapping": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>,
  "rigging-staging": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
  "sfx-laser": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  "technical-drawings": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  "3d-visualisation": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
};

const PillarIcons: Record<string, React.ReactNode> = {
  "ideas": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>,
  "innovation": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>,
  "technology": <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>,
};

const SERVICES = [
  { n: "01", slug: "audio",              name: "Audio Systems",      desc: "FOH, monitor, line array systems & microphones tuned for the room.",                    img: "/services/audio.jpg" },
  { n: "02", slug: "lighting",           name: "Lighting",           desc: "Moving heads, beams, washes & programmers — painting the stage with light.",           img: "/services/lighting.jpg" },
  { n: "03", slug: "led-video",          name: "LED & Video",        desc: "Indoor & outdoor LED walls, video switching, multi-screen output, live IMAG.",         img: "/services/led-video.jpg" },
  { n: "04", slug: "projection-mapping", name: "Projection Mapping", desc: "Pixel-perfect content mapped to surfaces, sculptures and facades.",                    img: "/services/projection-mapping.jpg" },
  { n: "05", slug: "rigging-staging",    name: "Rigging & Staging",  desc: "Certified truss, ground support, motors, custom stage builds & scenic.",               img: "/services/rigging-staging.jpg" },
  { n: "06", slug: "sfx-laser",          name: "SFX & Laser",        desc: "Cryo, confetti, CO2, low-fog, sparks & ILDA-class laser systems.",                     img: "/services/sfx-laser.jpg" },
  { n: "07", slug: "technical-drawings", name: "Technical Drawings", desc: "CAD plots, fixture plans, signal flow, weight loading & venue submissions.",           img: "/services/technical-drawings.jpg" },
  { n: "08", slug: "3d-visualisation",   name: "3D Visualisation",   desc: "Photo-real pre-vis of your show before a single cable is run.",                        img: "/services/3d-visualisation.jpg" },
];

const AV_SIM_IMG = "/services/av-simulation-studio.jpg";

const GALLERY_TEASE = [
  { slug: "emaar-gala-2024",   cat: "Government & Gala", title: "Emaar Gala 2024",       img: U("1492684223066-81342ee5ff30") },
  { slug: "sounds-of-arabia",  cat: "Live Concerts",     title: "Sounds of Arabia",      img: U("1429962714451-bb934ecdc4ec") },
  { slug: "gitex-2024",        cat: "Exhibitions",       title: "GITEX Global 2024",     img: U("1505373877841-8d25f7d46678") },
  { slug: "dubai-music-week",  cat: "Product Launches",        title: "Dubai Music Week",      img: U("1516450360452-9312f5e86fc7") },
  { slug: "national-day-show", cat: "Government & Gala", title: "National Day Showcase", img: U("1519167758481-83f550bb49b3") },
];

// ─── Hero Social Icons (bottom-left) — pulls URLs from site_settings ──
const InstagramPopup: React.FC<{ open: boolean; onClose: () => void; igUrl: string }> = ({ open, onClose, igUrl }) => {
  const [widgetFailed, setWidgetFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Load Elfsight platform script once
    if (!document.querySelector('script[src="https://elfsightcdn.com/platform.js"]')) {
      const s = document.createElement("script");
      s.src = "https://elfsightcdn.com/platform.js";
      s.async = true;
      document.body.appendChild(s);
    }
    // Freeze the page behind the popup (only the feed scrolls)
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Show the default system cursor over the popup (custom cursor can't render here)
    const hadCustomCursor = document.documentElement.classList.contains("iav-has-custom-cursor");
    if (hadCustomCursor) document.documentElement.classList.remove("iav-has-custom-cursor");
    const prevBodyCursor = document.body.style.cursor;
    document.body.style.cursor = "auto";
    // Fallback: if the Elfsight widget hasn't rendered content within 6s
    // (e.g. free-tier views exhausted, blocked, or offline), show a link-out.
    const t = setTimeout(() => {
      const app = document.querySelector(".elfsight-app-54de1813-89d3-4cf8-b67f-e6f83841dce6");
      if (!app || app.children.length === 0) setWidgetFailed(true);
    }, 6000);
    // Close on Escape
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      document.body.style.cursor = prevBodyCursor;
      if (hadCustomCursor) document.documentElement.classList.add("iav-has-custom-cursor");
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="iav-ig-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="ig-popup">
      <div className="iav-ig-modal">
        <div className="iav-ig-body">
          {widgetFailed ? (
            <div className="iav-ig-fallback">
              <p>See our latest work on Instagram.</p>
              <a href={igUrl} target="_blank" rel="noopener noreferrer" className="iav-btn iav-btn-primary">Open Instagram →</a>
            </div>
          ) : (
            <div className="elfsight-app-54de1813-89d3-4cf8-b67f-e6f83841dce6" data-elfsight-app-lazy />
          )}
        </div>
        <div className="iav-ig-foot">
          <a href={igUrl} target="_blank" rel="noopener noreferrer">View full profile on Instagram ↗</a>
        </div>
      </div>
    </div>,
    document.body
  );
};

const HeroSocials: React.FC = () => {
  const { settings } = useSettings();
  const ig = settings.instagram_url || "https://www.instagram.com/ideationav";
  const fb = settings.facebook_url || "";
  const li = settings.linkedin_url || "";
  const [igOpen, setIgOpen] = useState(false);
  if (!ig && !fb && !li) return null;
  return (
    <div className="iav-hero-social" aria-label="Social links">
      <span className="iav-hero-social-line" aria-hidden />
      {ig && (
        <button type="button" onClick={() => setIgOpen(true)} className="iav-hero-social-ico" aria-label="Instagram" data-testid="hero-instagram">
          <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
        </button>
      )}
      {fb && (
        <a href={fb} target="_blank" rel="noopener noreferrer" className="iav-hero-social-ico" aria-label="Facebook" data-testid="hero-facebook">
          <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        </a>
      )}
      {li && (
        <a href={li} target="_blank" rel="noopener noreferrer" className="iav-hero-social-ico" aria-label="LinkedIn" data-testid="hero-linkedin">
          <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
        </a>
      )}
      {settings.whatsapp_enabled === "true" && (settings.whatsapp_number || "").replace(/[^0-9]/g, "") && (
        <a
          href={`https://wa.me/${(settings.whatsapp_number || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(settings.whatsapp_message || "Hello Ideation AV")}`}
          target="_blank" rel="noopener noreferrer"
          className="iav-hero-social-ico" aria-label="Chat on WhatsApp" data-testid="hero-whatsapp"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.86 9.86 0 0 0 4.73 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.01A9.84 9.84 0 0 0 12.04 2zm0 18.13c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.19-.31a8.24 8.24 0 0 1-1.27-4.36c0-4.55 3.7-8.25 8.25-8.25 2.21 0 4.28.86 5.84 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.55-3.7 8.25-8.25 8.25zm4.52-6.18c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.76-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31s-.86.84-.86 2.05c0 1.21.88 2.38 1 2.55.12.17 1.74 2.66 4.21 3.73.59.25 1.05.4 1.41.51.59.19 1.13.16 1.55.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29z"/></svg>
        </a>
      )}
      <InstagramPopup open={igOpen} onClose={() => setIgOpen(false)} igUrl={ig} />
    </div>
  );
};

const Hero: React.FC = () => {
  const { settings } = useSettings();
  const videoUrl = (settings.hero_video_url || "").trim();
  const embedSrc = React.useMemo(() => {
    if (!videoUrl) return "";
    const m = videoUrl.match(/youtu\.?be(?:\.com)?\/(?:watch\?v=|embed\/|shorts\/)?([A-Za-z0-9_-]{6,})/i);
    if (m) {
      const id = m[1];
      return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&controls=0&playsinline=1&modestbranding=1&playlist=${id}`;
    }
    const v = videoUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    if (v) return `https://player.vimeo.com/video/${v[1]}?autoplay=1&muted=1&loop=1&background=1`;
    return "";
  }, [videoUrl]);

  const slides = React.useMemo(() => {
    const re = /^hero_slide_(\d+)$/;
    const fromSettings = Object.keys(settings)
      .map(k => { const m = k.match(re); return m ? { idx: parseInt(m[1], 10), url: (settings[k] || "").trim() } : null; })
      .filter((x): x is { idx: number; url: string } => !!x && x.url !== "")
      .sort((a, b) => a.idx - b.idx)
      .map(x => x.url);
    return fromSettings.length > 0 ? fromSettings : HERO_FALLBACK;
  }, [settings]);

  const [idx, setIdx] = useState(0);
  const idxRef = useRef(0);

  useEffect(() => {
    if (embedSrc) return; // Video mode — no slideshow timer.
    idxRef.current = 0; setIdx(0);
    if (slides.length < 2) return;
    const t = window.setInterval(() => {
      idxRef.current = (idxRef.current + 1) % slides.length;
      setIdx(idxRef.current);
    }, 6000);
    return () => window.clearInterval(t);
  }, [slides, embedSrc]);

  return (
    <section className="iav-hero" data-testid="hero">
      {embedSrc ? (
        <div className="iav-hero-video" aria-hidden data-testid="hero-video">
          <iframe
            src={embedSrc}
            title="Hero video"
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            frameBorder={0}
          />
        </div>
      ) : (
        <div className="iav-hero-slides" aria-hidden>
          {slides.map((src, i) => (
            <div
              key={src}
              className={`iav-hero-slide ${i === idx ? "is-active" : ""}`}
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
        </div>
      )}
      <div className="iav-hero-overlay" aria-hidden />
      <div className="iav-hero-grid-tex" aria-hidden />
      <div className="iav-hero-glow" aria-hidden />
      <div className="iav-hero-scan" aria-hidden />

      <div className="iav-hero-content">
        <span className="iav-hero-eyebrow reveal visible">Technical Production · Dubai · India</span>
        <h1 className="iav-hero-title reveal visible reveal-d1">
          We Engineer <span className="iav-accent">Extraordinary</span> Events.
        </h1>
        <p className="iav-hero-sub reveal visible reveal-d2">
          Audio · Lighting · LED · Projection Mapping · Rigging · Stage Design · SFX — delivered with precision across the Middle East and South Asia.
        </p>
        <div className="iav-hero-ctas reveal visible reveal-d3">
          <Link to="/gallery" className="iav-btn iav-btn-primary" data-testid="hero-cta-work"><span className="iav-dots">··</span> View Our Work</Link>
          <Link to="/quote" className="iav-btn iav-btn-secondary" data-testid="hero-cta-quote">Get a Quote</Link>
        </div>
      </div>

      <HeroSocials />

      {!embedSrc && (
        <div className="iav-hero-dots" role="tablist" aria-label="Hero slideshow">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`iav-hero-dot ${i === idx ? "is-active" : ""}`}
              onClick={() => { idxRef.current = i; setIdx(i); }}
              aria-label={`Show slide ${i + 1}`}
            />
          ))}
        </div>
      )}
      <div className="iav-hero-scroll" aria-hidden>
        <span>SCROLL</span>
        <span className="iav-hero-scroll-line" />
      </div>
    </section>
  );
};

// ─── Stats Bar with count-up ────────────────────────────────────────
const Counter: React.FC<{ target: number; trigger: boolean }> = ({ target, trigger }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    const start = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / 1500);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, trigger]);
  return <>{val}<span className="iav-stat-suffix">+</span></>;
};

const StatsBar: React.FC = () => {
  const { settings } = useSettings();
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { setInView(true); io.disconnect(); }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const num = (key: string, fallback: number): number => {
    const v = parseInt(settings[key] || "");
    return Number.isFinite(v) && v > 0 ? v : fallback;
  };
  const items: Array<[number, string]> = [
    [num("stats_events", 500),    "Events Delivered"],
    [num("stats_years", 10),      "Years of Excellence"],
    [num("stats_operations", 2),  "Global Operations"],
    [num("stats_services", 9),    "Core Services"],
  ];
  return (
    <section className="iav-stats" ref={ref} data-testid="stats-bar">
      <span className="iav-stats-topline" aria-hidden />
      <span className="iav-stats-pulse-h iav-stats-pulse-h-1" aria-hidden />
      <span className="iav-stats-pulse-h iav-stats-pulse-h-2" aria-hidden />
      <span className="iav-stats-bracket iav-stats-bracket-tl" aria-hidden />
      <span className="iav-stats-bracket iav-stats-bracket-tr" aria-hidden />
      <span className="iav-stats-bracket iav-stats-bracket-bl" aria-hidden />
      <span className="iav-stats-bracket iav-stats-bracket-br" aria-hidden />
      <div className="iav-stats-grid">
        {items.map(([target, label], i) => (
          <div key={label} className={`iav-stat reveal visible reveal-d${i + 1}`}>
            <div className="iav-stat-num"><Counter target={target} trigger={inView} /></div>
            <div className="iav-stat-label">{label}</div>
            {i < items.length - 1 && <span className="iav-stat-divider" aria-hidden />}
          </div>
        ))}
      </div>
    </section>
  );
};

// ─── About teaser ───────────────────────────────────────────────────
const AboutTeaser: React.FC = () => (
  <section className="iav-about-tease" data-testid="about-teaser">
    <div className="iav-about-tease-grid">
      <div className="iav-about-tease-vis" data-reveal>
        <div className="iav-about-img-wrap">
          <img src={ABOUT_IMG} alt="Live event production by Ideation AV" />
        </div>
        <div className="iav-about-badge">
          <strong>10+</strong>
          <span>YEARS OF<br/>TECHNICAL<br/>EXCELLENCE</span>
        </div>
      </div>
      <div className="iav-about-tease-text" data-reveal>
        <span className="iav-mono iav-tag-line">Who We Are</span>
        <h2 className="iav-about-heading">
          Built on precision.<br/>Driven by passion.
        </h2>
        <p className="iav-about-body">
          A technical production company headquartered in Dubai. We engineer audio, lighting, LED,
          projection mapping, rigging, staging and SFX for live events across the Middle East and South Asia.
        </p>
        <p className="iav-about-body">
          Every event is a new challenge. Every challenge is met with the same commitment — flawless
          execution from concept to close.
        </p>
        <div className="iav-cta-row" style={{marginTop: '30px'}}>
          <Link to="/about" className="iav-btn iav-btn-outline">Our Story →</Link>
        </div>
      </div>
    </div>
  </section>
);

// ─── Events We Power ────────────────────────────────────────────────
const EventsWePower: React.FC = () => {
  const { settings } = useSettings();
  return (
  <section className="iav-events" data-testid="events-grid">
    <div className="iav-events-inner">
      <header className="iav-section-head">
        <div data-reveal>
          <span className="iav-mono iav-tag-line">What We Do</span>
          <h2 className="iav-display iav-section-title">Events We <span className="iav-accent">Power.</span></h2>
        </div>
        <p className="iav-body iav-section-body" data-reveal>
          From intimate corporate gatherings to massive live concerts — every experience engineered with precision and passion.
        </p>
      </header>
      <div className="iav-events-grid">
        {EVENTS.map((e) => (
          <Link key={e.label} to={`/gallery?cat=${e.cat}`} className="iav-event-card" data-reveal data-cursor="cta">
            <img src={settings[e.settingKey] || e.img} alt={e.label} loading="lazy" />
            <div className="iav-event-overlay" aria-hidden />
            <div className="iav-event-label">{e.label}</div>
          </Link>
        ))}
      </div>
    </div>
  </section>
  );
};

// ─── Services Preview ───────────────────────────────────────────────
const ServicesPreview: React.FC = () => (
  <section className="iav-svc-prev" data-testid="services-preview">
    <div className="iav-svc-prev-inner">
      <header className="iav-svc-prev-header">
        <div data-reveal>
          <span className="iav-mono iav-tag-line">Our Services</span>
          <h2 className="iav-svc-heading">End-to-End Technical Solutions.</h2>
        </div>
        <Link to="/services" className="iav-btn iav-btn-outline" data-reveal>View All →</Link>
      </header>
      <div className="iav-svc-grid">
        {SERVICES.map((s, i) => (
          <Link
            key={s.slug}
            to={`/services/${s.slug}`}
            className="iav-svc-card iav-svc-card-image"
            data-reveal
            data-cursor="link"
            aria-label={`${s.name} — ${s.desc}`}
          >
            <div className="iav-svc-bg" style={{ backgroundImage: `url(${s.img})` }} aria-hidden />
            <span className="iav-svc-sr">{s.name}. {s.desc}</span>
          </Link>
        ))}
        {/* AV Simulation Studio — external app, mirrors the Services page so the grid has no empty cell */}
        <a
          href="https://www.avsimulationstudio.com"
          target="_blank"
          rel="noopener noreferrer"
          className="iav-svc-card iav-svc-card-image iav-svc-card-featured"
          data-reveal
          data-cursor="link"
          data-testid="home-service-av-simulation-studio"
          aria-label="AV Simulation Studio — 3D pre-visualisation for projection mapping, LED walls, truss and rigging"
        >
          <div className="iav-svc-bg" style={{ backgroundImage: `url(${AV_SIM_IMG})` }} aria-hidden />
          <span className="iav-svc-sr">AV Simulation Studio. 3D pre-visualisation for projection mapping, LED walls, truss and rigging — design your show before load-in. External link.</span>
        </a>
      </div>
    </div>
  </section>
);

// ─── Philosophy ─────────────────────────────────────────────────────
const Philosophy: React.FC = () => (
  <section className="iav-philo" data-testid="philosophy">
    <div className="iav-philo-vis" aria-hidden>
      <img src={PHILOSOPHY_IMG} alt="" />
      <div className="iav-philo-overlay" />
    </div>
    <div className="iav-philo-cont">
      <span className="iav-mono iav-tag-line" data-reveal>Our Foundation</span>
      <div className="iav-philo-block" data-reveal>
        <span className="iav-philo-lbl">Vision</span>
        <div className="iav-philo-title">Trusted Leader</div>
        <p className="iav-philo-text">
          To be a trusted leader in event technical services, known for delivering innovative and reliable
          solutions that elevate every experience.
        </p>
      </div>
      <div className="iav-philo-block" data-reveal>
        <span className="iav-philo-lbl">Mission</span>
        <div className="iav-philo-title">Flawless Execution</div>
        <p className="iav-philo-text">
          To provide seamless, high-quality technical services by combining expertise, advanced technology,
          and a commitment to excellence.
        </p>
      </div>
      <div className="iav-philo-pillars" data-reveal>
        {[
          { key: "ideas",      name: "Ideas",      txt: "Every event begins with a vision we shape together." },
          { key: "innovation", name: "Innovation", txt: "New approaches enhancing creativity and impact." },
          { key: "technology", name: "Technology", txt: "Advanced equipment, precision every time." },
        ].map(p => (
          <div key={p.key} className="iav-pillar">
            <div className="iav-svc-icon-wrap iav-pillar-ico-wrap">
              <span className="iav-svc-svg">{PillarIcons[p.key]}</span>
            </div>
            <div className="iav-pillar-name">{p.name}</div>
            <p className="iav-pillar-txt">{p.txt}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Gallery Teaser ─────────────────────────────────────────────────
const GalleryTease: React.FC = () => {
  const { settings } = useSettings();
  const cards = GALLERY_TEASE.map((base, idx) => {
    const n = idx + 1;
    return {
      img:   settings[`ourwork_${n}_img`]   || base.img,
      title: settings[`ourwork_${n}_title`] || base.title,
      cat:   settings[`ourwork_${n}_cat`]   || base.cat,
    };
  });
  return (
  <section className="iav-gal-tease" data-testid="gallery-teaser">
    <div className="iav-gal-tease-inner">
      <header className="iav-gal-head">
        <div data-reveal>
          <span className="iav-mono iav-tag-line">Portfolio</span>
          <h2 className="iav-gal-heading">Our Work.</h2>
        </div>
        <Link to="/gallery" className="iav-btn iav-btn-outline" data-reveal>View Full Gallery →</Link>
      </header>
      <div className="iav-gal-grid-masonry">
        {cards.map((p, i) => (
          <Link
            key={i}
            to="/gallery"
            className={`iav-gal-card-m ${i === 0 ? 'iav-gal-card-m--large' : ''}`}
            data-reveal
            data-cursor="cta"
          >
            <img src={p.img} alt={p.title} loading="lazy" />
            <div className="iav-gal-overlay" aria-hidden />
            <div className="iav-gal-meta">
              <span className="iav-gal-cat">{p.cat}</span>
              <span className="iav-gal-title-m">{p.title}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
  );
};

// ─── CTA ────────────────────────────────────────────────────────────
const CTASection: React.FC = () => (
  <section className="iav-cta-sect" data-testid="cta-section">
    <div className="iav-cta-glow" aria-hidden />
    <span className="iav-cta-pulse iav-cta-pulse-1" aria-hidden />
    <span className="iav-cta-pulse iav-cta-pulse-2" aria-hidden />
    <span className="iav-cta-bracket iav-cta-bracket-tl" aria-hidden />
    <span className="iav-cta-bracket iav-cta-bracket-tr" aria-hidden />
    <span className="iav-cta-bracket iav-cta-bracket-bl" aria-hidden />
    <span className="iav-cta-bracket iav-cta-bracket-br" aria-hidden />
    <div className="iav-cta-inner" data-reveal>
      <span className="iav-mono iav-tag-line iav-tag-center">Let's Create Together</span>
      <h2 className="iav-display iav-cta-title">
        READY TO <span className="iav-accent">ELEVATE</span><br />YOUR EVENT?
      </h2>
      <p className="iav-cta-sub">
        Great events don't just happen — they are carefully imagined, designed, and executed.
        Let's build something remarkable.
      </p>
      <div className="iav-cta-row iav-cta-row-center">
        <Link to="/quote" className="iav-btn iav-btn-primary" data-cursor="cta"><span className="iav-dots">··</span> Get a Quote</Link>
        <Link to="/contact" className="iav-btn iav-btn-outline" data-cursor="cta">Contact Us</Link>
      </div>
    </div>
  </section>
);

// ─── AV Simulation Studio Banner ────────────────────────────────────
const SimBanner: React.FC = () => {
  const { settings } = useSettings();

  const slides = React.useMemo(() => {
    const re = /^sim_slide_(\d+)$/;
    return Object.keys(settings)
      .map(k => { const m = k.match(re); return m ? { idx: parseInt(m[1], 10), url: (settings[k] || "").trim() } : null; })
      .filter((x): x is { idx: number; url: string } => !!x && x.url !== "")
      .sort((a, b) => a.idx - b.idx)
      .map(x => x.url);
  }, [settings]);

  const [idx, setIdx] = useState(0);
  const idxRef = useRef(0);

  useEffect(() => {
    idxRef.current = 0; setIdx(0);
    if (slides.length < 2) return;
    const t = window.setInterval(() => {
      idxRef.current = (idxRef.current + 1) % slides.length;
      setIdx(idxRef.current);
    }, 4000);
    return () => window.clearInterval(t);
  }, [slides.length]);

  if (settings.sim_banner_visible === "false") return null;

  const label = settings.sim_banner_label || "NEW LAUNCH";
  const heading = settings.sim_banner_heading || "AV Simulation Studio";
  const description = settings.sim_banner_description || "Pre-visualise your entire show — projection mapping, LED walls, truss and rigging — before load-in. Professional 3D simulation built for AV designers and event producers. Powered by Ideation Audio Visual.";
  const buttonLabel = settings.sim_banner_button_label || "Launch App";
  const buttonUrl = settings.sim_banner_button_url || "https://www.avsimulationstudio.com";

  return (
    <section className="iav-sim-banner" data-testid="sim-banner">
      <div className="iav-sim-banner-grid">
        <div className="iav-sim-banner-text" data-reveal>
          <span className="iav-sim-banner-label">
            <span className="iav-sim-banner-dot" aria-hidden />
            {label}
          </span>
          <h2 className="iav-sim-banner-heading">{heading}</h2>
          <p className="iav-sim-banner-desc">{description}</p>
          <a
            href={buttonUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="iav-sim-banner-btn"
            data-cursor="cta"
            data-testid="sim-banner-btn"
          >
            {buttonLabel} <span aria-hidden>→</span>
          </a>
        </div>
        <div className="iav-sim-banner-vis" data-reveal data-testid="sim-banner-vis">
          {slides.length === 0 ? (
            <div className="iav-sim-banner-placeholder" aria-hidden>
              <span>Upload screenshots in admin</span>
            </div>
          ) : slides.length === 1 ? (
            <img src={slides[0]} alt={heading} className="iav-sim-banner-img" />
          ) : (
            <>
              {slides.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={`${heading} ${i + 1}`}
                  className={`iav-sim-slide ${i === idx ? "is-active" : ""}`}
                />
              ))}
              <div className="iav-sim-dots" role="tablist" aria-label="Slideshow">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`iav-sim-dot ${i === idx ? "is-active" : ""}`}
                    onClick={() => { idxRef.current = i; setIdx(i); }}
                    aria-label={`Show slide ${i + 1}`}
                    data-testid={`sim-dot-${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

const Home: React.FC = () => {
  useReveal();
  const [showTop, setShowTop] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <main className="iav-home">
      <Hero />
      <StatsBar />
      <AboutTeaser />
      <EventsWePower />
      <ServicesPreview />
      <Philosophy />
      <SimBanner />
      <GalleryTease />
      <CTASection />
      {showTop && (
        <button
          className="iav-scroll-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </main>
  );
};

export default Home;
